import { useEffect, useMemo, useRef, useState } from 'react';

import { PickTaskFilters } from './PickTaskFilterBar';
import {
  EMPTY_FILTERS,
  matchAdvanced,
  PICK_TASK_COLUMN_ITEMS,
  SORT_KEYS,
} from './pickTaskListConfig';
import { RepSort } from './PickTaskTable';
import { PickTask } from '../../../@types/picktask.type';
import { ColumnSelectionItem } from '../../../components/templates/ColumnSelection';
import { useGetPickTasks } from '../../../queries/picktask-query';

// Owns all list state, the client-side filter/sort/paginate derivation, and the
// row/column/report handlers so the page component stays presentational.
export const usePickTaskListState = () => {
  const [filters, setFilters] = useState<PickTaskFilters>(EMPTY_FILTERS);
  const [sortModel, setSortModel] = useState<RepSort>();
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [columnSelection, setColumnSelection] =
    useState<ColumnSelectionItem[]>(PICK_TASK_COLUMN_ITEMS);
  const [autoRefreshInterval, setAutoRefreshInterval] = useState(0);
  const [fetchDurationMs, setFetchDurationMs] = useState<number | null>(null);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const startRef = useRef<number>(performance.now());

  const { data, refetch } = useGetPickTasks();

  useEffect(() => {
    setFetchDurationMs(performance.now() - startRef.current);
  }, [data]);

  // Auto-refresh on the chosen interval (0 = off).
  useEffect(() => {
    if (autoRefreshInterval <= 0) return;
    const timer = setInterval(() => {
      startRef.current = performance.now();
      refetch();
    }, autoRefreshInterval * 1000);
    return () => clearInterval(timer);
  }, [autoRefreshInterval, refetch]);

  // Filter (status / multi-search / advanced) -> sort -> paginate, all client-side.
  const { pageRows, total } = useMemo(() => {
    const all = (data ?? []) as PickTask[];
    const q = filters.multiSearch.trim().toLowerCase();
    const filtered = all.filter((row) => {
      const r = row as unknown as Record<string, unknown>;
      if (filters.statuses.length && !filters.statuses.includes(row.status)) return false;
      if (q) {
        const hay = [
          row.id,
          row.status,
          row.allocatedToUserName,
          row.fromLocationName,
          row.toLocationName,
          row.createdAt,
        ]
          .map((x) => String(x ?? ''))
          .join(' ')
          .toLowerCase();
        if (!hay.includes(q)) return false;
      }
      if (filters.advancedFilters.length) {
        const ok = filters.advancedFilters.every((f) =>
          matchAdvanced(r[f.dataKey || f.field], f.operator, f.value)
        );
        if (!ok) return false;
      }
      return true;
    });

    const sorted = sortModel
      ? [...filtered].sort((a, b) => {
          const key = SORT_KEYS[sortModel.field] ?? sortModel.field;
          const av = (a as unknown as Record<string, unknown>)[key];
          const bv = (b as unknown as Record<string, unknown>)[key];
          const cmp =
            typeof av === 'number' && typeof bv === 'number'
              ? av - bv
              : String(av ?? '').localeCompare(String(bv ?? ''));
          return sortModel.sort === 'asc' ? cmp : -cmp;
        })
      : filtered;

    return {
      total: sorted.length,
      pageRows: sorted.slice(page * pageSize, page * pageSize + pageSize),
    };
  }, [data, filters, sortModel, page, pageSize]);

  // Keep the current page in range if a refetch shrinks the result set or the
  // page size grows, so we never render an empty page past the end.
  useEffect(() => {
    const maxPage = Math.max(0, Math.ceil(total / Math.max(1, pageSize)) - 1);
    setPage((current) => (current > maxPage ? maxPage : current));
  }, [total, pageSize]);

  const onFiltersChange = (next: PickTaskFilters) => {
    setFilters(next);
    setPage(0);
  };

  const onSearch = () => {
    startRef.current = performance.now();
    refetch();
  };

  const onReset = () => {
    setFilters(EMPTY_FILTERS);
    setPage(0);
    startRef.current = performance.now();
    refetch();
  };

  const onSortChange = (field: string) =>
    setSortModel((prev) =>
      prev?.field === field
        ? { field, sort: prev.sort === 'asc' ? 'desc' : 'asc' }
        : { field, sort: 'asc' }
    );

  const onToggleRow = (id: number) =>
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const onToggleAll = (checked: boolean) => {
    const ids = pageRows.map((r) => Number(r.id));
    setSelectedIds((prev) =>
      checked ? [...new Set([...prev, ...ids])] : prev.filter((id) => !ids.includes(id))
    );
  };

  const onSelectRange = (ids: number[]) =>
    setSelectedIds((prev) => [...new Set([...prev, ...ids])]);

  const onReorder = (activeField: string, overField: string) =>
    setColumnSelection((prev) =>
      prev.map((group) => {
        const from = group.value.findIndex((f) => f.field === activeField);
        const to = group.value.findIndex((f) => f.field === overField);
        if (from === -1 || to === -1) return group;
        const list = [...group.value];
        const [moved] = list.splice(from, 1);
        list.splice(to, 0, moved);
        return { ...group, value: list };
      })
    );

  // The column picker's "Reset to default". This list keeps its columns in
  // component state only (no sessionStorage layer, unlike the Parts/Jobs
  // lists), so restoring the built-in set is the whole of it.
  const resetColumnSelection = () => setColumnSelection(PICK_TASK_COLUMN_ITEMS);

  // Apply a saved report's snapshot (filters/sort/pageSize/columns) to the page.
  const applyReportSnapshot = (
    snapFilters: PickTaskFilters,
    snapSort: RepSort | undefined,
    snapPageSize: number,
    snapColumns?: ColumnSelectionItem[]
  ) => {
    setFilters(snapFilters);
    setSortModel(snapSort);
    setPageSize(snapPageSize);
    if (snapColumns) setColumnSelection(snapColumns);
    setPage(0);
  };

  return {
    filters,
    sortModel,
    page,
    setPage,
    pageSize,
    setPageSize,
    columnSelection,
    setColumnSelection,
    resetColumnSelection,
    autoRefreshInterval,
    setAutoRefreshInterval,
    fetchDurationMs,
    selectedIds,
    pageRows,
    total,
    onFiltersChange,
    onSearch,
    onReset,
    onSortChange,
    onToggleRow,
    onToggleAll,
    onSelectRange,
    onReorder,
    applyReportSnapshot,
  };
};
